<template>
  <div class="page-view">

    <BasePageHeader :title="$t('projects.calendar')" @back="router.back()">
      <template #actions>
        <button class="btn ghost small" :title="$t('projects.listView')" @click="router.push('/projects')">
          <MdiViewList class="w-4 h-4 mr-1" />
          {{ $t('projects.listView') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Quick filters -->
    <div class="flex gap-1.5 px-4 pt-3 pb-1 overflow-x-auto [scrollbar-width:none] [-webkit-overflow-scrolling:touch]">
      <button
        v-for="f in QUICK_FILTERS"
        :key="f.value"
        :class="['flex-shrink-0 px-3 py-1.5 rounded-full border text-[.8rem] font-medium cursor-pointer transition-all', quickFilter === f.value ? 'bg-blue-700 border-blue-500 text-white' : 'bg-gray-800 border-gray-600 text-gray-400 hover:bg-[#273344] hover:border-gray-500 hover:text-gray-200']"
        @click="quickFilter = f.value"
      >{{ $t('projects.filters.' + f.value) }}</button>
    </div>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <template v-else>

      <!-- Window info + legend -->
      <div class="flex items-center gap-3 px-4 pt-2 pb-1 text-xs text-[var(--color-muted)]">
        <span>{{ fmtDate(windowStart) }} — {{ fmtDate(windowEnd) }}</span>
        <button
          class="btn ghost small ml-auto"
          @click="() => scrollToToday()"
        >
          <MdiCalendarToday class="w-3.5 h-3.5 mr-1" />
          {{ $t('common.today') }}
        </button>
      </div>

      <!-- ── Gantt chart ──────────────────────────────────────────── -->
      <div
        ref="ganttEl"
        class="mx-4 mt-1 mb-6 rounded-xl border border-gray-800 overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-600 select-none"
        :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
        @mousedown="onGanttMouseDown"
        @mousemove="onGanttMouseMove"
        @mouseup="onGanttMouseUp"
        @mouseleave="onGanttMouseUp"
      >
        <div :style="{ width: (LABEL_W + totalWidth) + 'px' }">

          <!-- Month header row -->
          <div
            class="flex border-b border-gray-800 bg-[var(--color-surface-2)]"
            style="height: 28px"
          >
            <!-- Corner cell (sticky left) -->
            <div
              class="shrink-0 sticky left-0 z-[1] border-r border-gray-800 bg-[var(--color-surface-2)]"
              :style="{ width: LABEL_W + 'px', minWidth: LABEL_W + 'px' }"
            />
            <!-- Month labels -->
            <div class="relative" :style="{ width: totalWidth + 'px', flex: 'none' }">
              <div
                v-for="m in months"
                :key="m.label"
                class="absolute top-0 bottom-0 flex items-center pl-1.5 text-[0.67rem] text-[var(--color-muted)] font-medium tracking-wide border-l border-gray-800 whitespace-nowrap overflow-hidden"
                :style="{ left: m.left + 'px', width: m.width + 'px' }"
              >{{ m.label }}</div>
              <!-- Today marker in header -->
              <div
                class="absolute top-0 bottom-0 w-0.5 bg-emerald-500/40"
                :style="{ left: todayX + 'px' }"
              />
            </div>
          </div>

          <!-- Project rows -->
          <div
            v-for="p in visibleDated"
            :key="p.id"
            class="flex border-b border-gray-800/30"
            style="height: 44px"
          >
            <!-- Sticky label cell -->
            <div
              class="sticky left-0 z-[1] shrink-0 flex flex-col justify-center px-3 border-r border-gray-800 cursor-pointer transition-colors hover:bg-gray-800/60 bg-[var(--color-surface)]"
              :style="{ width: LABEL_W + 'px', minWidth: LABEL_W + 'px' }"
              @click="navToProject(p.id)"
            >
              <div class="text-[0.78rem] font-medium leading-snug truncate">{{ p.name }}</div>
              <div class="text-[0.65rem] text-[var(--color-muted)] truncate">
                {{ projectStatusLabel(p.status, p.status_display) }}{{ customerLookup[resolveCustomerId(p)] ? ' · ' + customerLookup[resolveCustomerId(p)] : '' }}
              </div>
            </div>

            <!-- Timeline area -->
            <div class="relative" :style="{ width: totalWidth + 'px', flex: 'none' }">
              <!-- Week guide lines -->
              <div
                v-for="wx in weekGuides"
                :key="wx"
                class="absolute top-0 bottom-0 w-px bg-white/[0.04]"
                :style="{ left: wx + 'px' }"
              />
              <!-- Today line -->
              <div
                class="absolute top-0 bottom-0 w-0.5 bg-emerald-500/40 z-[5]"
                :style="{ left: todayX + 'px' }"
              />
              <!-- Project bar -->
              <div
                v-if="barsMap.get(p.id)"
                class="absolute top-2 rounded-md flex items-center px-2 text-[0.7rem] font-medium cursor-pointer overflow-hidden transition-opacity hover:opacity-75 select-none"
                :class="statusBarClass(p.status)"
                :style="{ left: barsMap.get(p.id).x + 'px', width: barsMap.get(p.id).w + 'px', height: '28px' }"
                :title="p.name + (customerLookup[resolveCustomerId(p)] ? ' · ' + customerLookup[resolveCustomerId(p)] : '') + '\n' + fmtDate(p.start_time) + ' – ' + (p.end_time ? fmtDate(p.end_time) : '?')"
                @click="navToProject(p.id)"
              >
              </div>
            </div>
          </div>

          <!-- Empty state row -->
          <div v-if="!visibleDated.length" class="flex" style="height: 80px">
            <div
              class="sticky left-0 z-[1] shrink-0 bg-[var(--color-surface)]"
              :style="{ width: LABEL_W + 'px', minWidth: LABEL_W + 'px' }"
            />
            <div class="flex flex-1 items-center justify-center text-sm text-[var(--color-muted)]">
              {{ $t('projects.noDatesInWindow', { entity: $t('entity.project_plural') }) }}
            </div>
          </div>

        </div>
      </div>

      <!-- ── Status legend ──────────────────────────────────────────── -->
      <div class="flex flex-wrap gap-x-3 gap-y-1.5 px-4 mb-4 text-xs text-[var(--color-muted)]">
        <span v-for="s in STATUS_LEGEND" :key="s.label" class="flex items-center gap-1.5">
          <span class="inline-block w-3 h-3 rounded-sm border" :class="s.cls"></span>
          {{ s.label }}
        </span>
      </div>

      <!-- ── Undated projects ────────────────────────────────────────── -->
      <template v-if="undated.length">
        <div class="px-4"><div class="section-label">{{ $t('projects.noDates') }}</div></div>
        <div class="list-stack pb-4">
          <div
            v-for="p in undated"
            :key="p.id"
            class="data-card cursor-pointer"
            @click="router.push('/project/' + p.id)"
          >
            <div class="data-card__main">
              <div class="data-card__name">{{ p.name }}</div>
              <div class="data-card__meta">
                <span v-if="customerLookup[resolveCustomerId(p)]" class="badge badge-muted">
                  {{ customerLookup[resolveCustomerId(p)] }}
                </span>
                <span class="badge" :class="statusBadgeClass(p.status)">{{ projectStatusLabel(p.status, p.status_display) }}</span>
              </div>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-gray-500 shrink-0">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </div>
      </template>

    </template>

  </div>
</template>

<script setup>
import { useStatusLabels } from '@/composables/useStatusLabels.js'
const { projectStatusLabel } = useStatusLabels()

import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { getProjects, getCustomers } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import MdiViewList from '~icons/mdi/view-list'
import MdiCalendarToday from '~icons/mdi/calendar-today'
import { useI18n } from 'vue-i18n'

const router = useRouter()

const { t } = useI18n()

// ── Scroll ref & drag-to-scroll ─────────────────────────────────────
const ganttEl    = ref(null)
const isDragging   = ref(false)
const _didDrag     = ref(false)
let   _dragX       = 0
let   _dragScrollLeft = 0

function onGanttMouseDown(e) {
  if (e.button !== 0) return
  isDragging.value  = true
  _didDrag.value    = false
  _dragX            = e.pageX
  _dragScrollLeft   = ganttEl.value.scrollLeft
}
function onGanttMouseMove(e) {
  if (!isDragging.value) return
  const dx = e.pageX - _dragX
  if (Math.abs(dx) > 3) _didDrag.value = true
  e.preventDefault()
  ganttEl.value.scrollLeft = _dragScrollLeft - dx
}
function onGanttMouseUp() { isDragging.value = false }
function navToProject(id) { if (!_didDrag.value) router.push('/project/' + id) }

// How many px of past context to keep visible to the left of today
const SCROLL_TODAY_MARGIN = 80

function scrollToToday(behavior = 'smooth') {
  if (!ganttEl.value) return
  const offset = Math.max(0, todayX - SCROLL_TODAY_MARGIN)
  ganttEl.value.scrollTo({ left: offset, behavior })
}

// ── Constants ──────────────────────────────────────────────────────────
const LABEL_W    = 160
const PX_PER_DAY = 10

const QUICK_FILTERS = computed(() => [
  { value: 'active',   label: t('common.active') },
  { value: 'upcoming', label: t('projects.filters.upcoming') },
  { value: 'all',      label: t('common.all') },
])

const STATUS_LEGEND = computed(() => [
  { label: t('projects.statusLegend.draft'),     cls: 'bg-gray-600/60 border-gray-500/30' },
  { label: t('projects.statusLegend.confirmed'), cls: 'bg-blue-700/70 border-blue-500/30' },
  { label: t('projects.statusLegend.ready'),     cls: 'bg-green-700/70 border-green-500/30' },
  { label: t('projects.statusLegend.pickedUp'),  cls: 'bg-yellow-700/70 border-yellow-600/30' },
  { label: t('projects.statusLegend.delivered'), cls: 'bg-orange-700/70 border-orange-500/30' },
  { label: t('projects.statusLegend.active'),    cls: 'bg-emerald-700/70 border-emerald-500/30' },
  { label: t('projects.statusLegend.returning'), cls: 'bg-violet-700/70 border-violet-500/30' },
  { label: t('projects.statusLegend.completed'), cls: 'bg-gray-700/40 border-gray-600/30' },
  { label: t('projects.statusLegend.cancelled'), cls: 'bg-red-900/60 border-red-700/30' },
])

// ── State ──────────────────────────────────────────────────────────────
const projects       = ref([])
const customerLookup = ref({})
const loading        = ref(false)
const loadError      = ref(null)
const quickFilter    = ref('active')

// ── Time window (3 months back → 9 months forward) ────────────────────
const today = new Date()
today.setHours(0, 0, 0, 0)

const windowStart = new Date(today.getFullYear(), today.getMonth() - 3, 1)
const windowEnd   = new Date(today.getFullYear(), today.getMonth() + 10, 0) // last day of month+9
const totalDays   = Math.floor((windowEnd - windowStart) / 86400000)
const totalWidth  = totalDays * PX_PER_DAY
const todayX      = Math.floor((today - windowStart) / 86400000) * PX_PER_DAY

// ── Month header labels ────────────────────────────────────────────────
const months = computed(() => {
  const result = []
  const cursor = new Date(windowStart.getFullYear(), windowStart.getMonth(), 1)
  while (cursor <= windowEnd) {
    const monthEnd     = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)
    const clampedStart = cursor   < windowStart ? windowStart : new Date(cursor)
    const clampedEnd   = monthEnd > windowEnd   ? windowEnd   : monthEnd
    const leftDays     = Math.floor((clampedStart - windowStart) / 86400000)
    const widthDays    = Math.floor((clampedEnd   - clampedStart) / 86400000) + 1
    result.push({
      label: cursor.toLocaleString('default', { month: 'short', year: '2-digit' }),
      left:  leftDays  * PX_PER_DAY,
      width: widthDays * PX_PER_DAY,
    })
    cursor.setMonth(cursor.getMonth() + 1)
  }
  return result
})

// ── Week guide marks ───────────────────────────────────────────────────
const weekGuides = computed(() => {
  const guides = []
  const cursor = new Date(windowStart)
  const dow    = cursor.getDay()
  cursor.setDate(cursor.getDate() + ((8 - dow) % 7 || 7)) // advance to next Monday
  while (cursor <= windowEnd) {
    guides.push(Math.floor((cursor - windowStart) / 86400000) * PX_PER_DAY)
    cursor.setDate(cursor.getDate() + 7)
  }
  return guides
})

// ── Filter helpers ─────────────────────────────────────────────────────
function resolveCustomerId(p) {
  return typeof p.customer === 'object' && p.customer !== null ? p.customer.id : p.customer
}

const filtered = computed(() => {
  let list = projects.value
  if (quickFilter.value === 'active') {
    list = list.filter(p => p.status !== 7 && p.status !== 8)
  } else if (quickFilter.value === 'upcoming') {
    list = list.filter(p => p.start_time && new Date(p.start_time) >= today && p.status !== 8)
  }
  return list
})

const visibleDated = computed(() =>
  filtered.value
    .filter(p => p.start_time)
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
)

const undated = computed(() => filtered.value.filter(p => !p.start_time))

// ── Bar positions ──────────────────────────────────────────────────────
const barsMap = computed(() => {
  const map = new Map()
  for (const p of visibleDated.value) {
    const start = new Date(p.start_time)
    // If no end_time, treat as at least 1 day wide
    const end   = p.end_time ? new Date(p.end_time) : new Date(start.getTime() + 86400000)
    if (start > windowEnd || end < windowStart) continue

    const clampedStart = start < windowStart ? windowStart : start
    const clampedEnd   = end   > windowEnd   ? windowEnd   : end
    const x = Math.floor((clampedStart - windowStart) / 86400000) * PX_PER_DAY
    const w = Math.max(PX_PER_DAY, Math.floor((clampedEnd - clampedStart) / 86400000) * PX_PER_DAY)
    map.set(p.id, { x, w })
  }
  return map
})

// ── Status styling ─────────────────────────────────────────────────────
function statusBarClass(status) {
  return ({
    0: 'bg-gray-600/60 text-gray-200 border border-gray-500/30',
    1: 'bg-blue-700/70 text-blue-100 border border-blue-500/30',
    2: 'bg-green-700/70 text-green-100 border border-green-500/30',
    3: 'bg-yellow-700/70 text-yellow-100 border border-yellow-600/30',
    4: 'bg-orange-700/70 text-orange-100 border border-orange-500/30',
    5: 'bg-emerald-700/70 text-emerald-100 border border-emerald-500/30',
    6: 'bg-violet-700/70 text-violet-100 border border-violet-500/30',
    7: 'bg-gray-700/40 text-gray-400 border border-gray-600/30',
    8: 'bg-red-900/60 text-red-200 border border-red-700/30',
  }[status] ?? 'bg-gray-600/60 text-gray-200 border border-gray-500/30')
}

function statusBadgeClass(status) {
  if (status === 0) return 'badge-muted'
  if (status === 1) return 'badge-accent'
  if ([2, 5, 7].includes(status)) return 'badge-success'
  if ([3, 4, 6].includes(status)) return 'badge-warning'
  if (status === 8) return 'badge-danger'
  return 'badge-muted'
}

function fmtDate(d) {
  if (!d) return ''
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Data loading ───────────────────────────────────────────────────────
async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const [projRes, custRes] = await Promise.all([getProjects(), getCustomers()])
    projects.value = projRes.results ?? projRes ?? []
    const custList = custRes.results ?? custRes ?? []
    const lookup   = {}
    for (const c of custList) lookup[c.id] = c.name
    customerLookup.value = lookup
    loading.value = false
    await nextTick()
    scrollToToday('instant')
  } catch {
    loadError.value = t('projects.loadFailed')
    loading.value = false
  }
}

onMounted(load)
</script>
